import { useEffect, useRef } from 'react';
import { SessionState } from 'sip.js';
import { pageAuthorities } from '@/shared/auth/has-any-authority';
import { useHasAnyAuthority } from '@/shared/auth/use-has-any-authority';
import { APP_PERMISSIONS } from '@/shared/constants/app-permissions';
import { agentPanelActions } from '@/shared/reducers/casePanel.reducer';
import { extractPhoneDigits } from '@/shared/services/agentPanelPhone.mapper';
import { useSipCall } from '@/shared/providers/SipCallProvider';
import { useAppDispatch } from '@/store';
/** Delay after answer before queue CONNECT log is available on the backend. */
const INBOUND_QUEUE_LOG_FETCH_DELAY_MS = 4_000;
/**
 * On inbound SIP ring: populate agent-panel Redux with caller context and run lookup —
 * without navigating away from the agent's current page.
 *
 * Queue CONNECT log is fetched after the agent answers (see delayed fetch effect below).
 *
 * Customer 360 registration UI lives only on {@link AgentPanelPage} (`agent-panel.tsx`).
 */
export function AgentInboundCallBridge() {
    const dispatch = useAppDispatch();
    const { incomingRing, isOnBreak, conversationUi } = useSipCall();
    const canUseAgentPanel = useHasAnyAuthority(pageAuthorities(APP_PERMISSIONS.CASEMANAGEMENT_AGENTPANEL));
    const processedDigitsRef = useRef(null);
    const queueLogFetchTimeoutRef = useRef(null);
    const queueLogScheduledDigitsRef = useRef(null);
    const queueLogFetchedDigitsRef = useRef(null);
    useEffect(() => {
        if (!canUseAgentPanel || isOnBreak) {
            processedDigitsRef.current = null;
            return;
        }
        const callerLabel = incomingRing?.callerLabel;
        if (!callerLabel) {
            processedDigitsRef.current = null;
            return;
        }
        const digits = extractPhoneDigits(callerLabel);
        if (!digits || processedDigitsRef.current === digits) {
            return;
        }
        processedDigitsRef.current = digits;
        dispatch(agentPanelActions.clearAgentCoreBanking());
        dispatch(agentPanelActions.setInboundCallingNumber(digits));
        dispatch(agentPanelActions.setAgentPanelPhone({ number: digits }));
        void dispatch(agentPanelActions.phoneLookup(digits));
        void dispatch(agentPanelActions.getIvrTracksBySrc(digits));
    }, [canUseAgentPanel, dispatch, incomingRing?.callerLabel, isOnBreak]);
    useEffect(() => {
        const clearQueueLogTimeout = () => {
            if (queueLogFetchTimeoutRef.current) {
                clearTimeout(queueLogFetchTimeoutRef.current);
                queueLogFetchTimeoutRef.current = null;
            }
            queueLogScheduledDigitsRef.current = null;
        };
        if (!canUseAgentPanel || isOnBreak) {
            clearQueueLogTimeout();
            return;
        }
        const isInboundEstablished = conversationUi?.direction === 'inbound' && conversationUi.sipState === SessionState.Established;
        if (!isInboundEstablished) {
            clearQueueLogTimeout();
            if (!conversationUi) {
                queueLogFetchedDigitsRef.current = null;
            }
            return;
        }
        const digits = processedDigitsRef.current?.trim() ||
            extractPhoneDigits(conversationUi.remoteLabel);
        if (!digits) {
            return;
        }
        if (queueLogFetchedDigitsRef.current === digits ||
            queueLogScheduledDigitsRef.current === digits) {
            return;
        }
        queueLogScheduledDigitsRef.current = digits;
        queueLogFetchTimeoutRef.current = setTimeout(() => {
            queueLogFetchTimeoutRef.current = null;
            queueLogScheduledDigitsRef.current = null;
            queueLogFetchedDigitsRef.current = digits;
            void dispatch(agentPanelActions.fetchInboundQueueLog(digits));
        }, INBOUND_QUEUE_LOG_FETCH_DELAY_MS);
        return clearQueueLogTimeout;
    }, [canUseAgentPanel, conversationUi, dispatch, isOnBreak]);
    return null;
}
